import Phaser from 'phaser';
import { FONT_FAMILY, FONT } from './FontLoader';

export interface HealthBarOptions {
  width: number;
  height: number;
  showText?: boolean;
  depth?: number;
}

export class HealthBar {
  private scene: Phaser.Scene;
  private x: number;
  private y: number;
  private width: number;
  private height: number;
  private bg: Phaser.GameObjects.Rectangle;
  private fill: Phaser.GameObjects.Rectangle;
  private segments: Phaser.GameObjects.Rectangle[] = [];
  private text?: Phaser.GameObjects.Text;
  private currentHp = 0;
  private maxHp = 1;

  constructor(scene: Phaser.Scene, x: number, y: number, opts: HealthBarOptions) {
    this.scene = scene;
    this.x = x;
    this.y = y;
    this.width = opts.width;
    this.height = opts.height;
    const depth = opts.depth ?? 10;

    // Background
    this.bg = scene.add.rectangle(x, y, opts.width, opts.height, 0x0a0a14, 0.9)
      .setStrokeStyle(1, 0x444466)
      .setDepth(depth);

    // Fill bar (left-aligned)
    this.fill = scene.add.rectangle(
      x - opts.width / 2, y, 0, opts.height - 2, 0x44cc66, 1,
    ).setOrigin(0, 0.5).setDepth(depth + 1);

    // Text label
    if (opts.showText !== false) {
      this.text = scene.add.text(x, y, '', {
        fontFamily: FONT_FAMILY,
        fontSize: FONT.tiny,
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 2,
      }).setOrigin(0.5).setDepth(depth + 3);
    }
  }

  update(current: number, max: number): void {
    const prevHp = this.currentHp;
    this.currentHp = current;
    this.maxHp = max;

    const pct = Math.max(0, Math.min(1, current / max));
    const targetWidth = Math.round(pct * (this.width - 2));

    // Color gradient: green > yellow > red
    const color = this.getHealthColor(pct);
    this.fill.setFillStyle(color);

    // Animate width change
    this.scene.tweens.add({
      targets: this.fill,
      displayWidth: targetWidth,
      duration: 200,
      ease: 'Quad.easeOut',
    });

    // Flash on damage
    if (current < prevHp && prevHp > 0) {
      this.flash();
    }

    // Update segments
    this.updateSegments(max);

    // Update text
    if (this.text) {
      this.text.setText(`${current}/${max}`);
    }
  }

  private getHealthColor(pct: number): number {
    if (pct > 0.5) {
      // Green to yellow
      const t = (pct - 0.5) * 2;
      return Phaser.Display.Color.GetColor(
        Math.round(68 + (1 - t) * 187),
        Math.round(204 - (1 - t) * 4),
        Math.round(102 - (1 - t) * 36),
      );
    }
    // Yellow to red
    const t = pct * 2;
    return Phaser.Display.Color.GetColor(
      Math.round(255 - t * 0),
      Math.round(68 + t * 132),
      Math.round(68 - (1 - t) * 2),
    );
  }

  private updateSegments(max: number): void {
    // Clear old segments
    for (const seg of this.segments) seg.destroy();
    this.segments = [];

    // Draw segment lines every 10% of max
    const segCount = 10;
    const depth = this.bg.depth + 2;
    for (let i = 1; i < segCount; i++) {
      const sx = this.x - this.width / 2 + (this.width - 2) * (i / segCount);
      const seg = this.scene.add.rectangle(sx, this.y, 1, this.height - 2, 0x000000, 0.3)
        .setDepth(depth);
      this.segments.push(seg);
    }
  }

  private flash(): void {
    const originalColor = this.fill.fillColor;
    this.fill.setFillStyle(0xffffff);
    this.scene.time.delayedCall(80, () => {
      this.fill.setFillStyle(originalColor);
    });
  }

  setDepth(depth: number): void {
    this.bg.setDepth(depth);
    this.fill.setDepth(depth + 1);
    for (const seg of this.segments) seg.setDepth(depth + 2);
    if (this.text) this.text.setDepth(depth + 3);
  }

  destroy(): void {
    this.bg.destroy();
    this.fill.destroy();
    for (const seg of this.segments) seg.destroy();
    if (this.text) this.text.destroy();
  }
}
